/*
 *
 * Profile
 *
 */

import "../styles.scss";

import { API, Endpoints } from "utils/api";
import { Alert, Checkbox, Col, DatePicker, Input, Row, Select, Tabs, notification } from "antd";
import { Icons, PhoneInput } from "components";
import { setBreadcrumb, setLoader, setUser } from "store/actions";

import Compressor from "compressorjs";
import Dropzone from "react-dropzone";
import { Footer } from "screens";
import { Helmet } from "react-helmet";
import Password from "components/Password";
import ProfileImagePlaceholder from "assets/images/placeholders/user2x.jpg";
import React from "react";
import Strings from "utils/strings";
// @ts-ignore
import Variables from "styles/variables.scss";
import { connect } from "react-redux";
import { emailIsValid } from "utils/utils";
import moment from "moment";
import { replace } from "connected-react-router";

export class Profile extends React.Component<any, any> {
	constructor(props: any) {
		super(props);

		this.state = {
			activeTab: "info",

			name: props.user.name,
			email: props.user.email,
			phone: props.user.phone,
			photo: props.user.photo,
			birthdate: props.user.birthdate,

			newEmail: "",

			password: "",
			newPassword: "",
			showPassword: false,
			showNewPassword: false,
			marketingEmail: props.user.marketingEmail || false,
			marketingNotification: props.user.marketingNotification || false,

			generalChanged: false,
			passwordChanged: false,
			savingProfile: false,
			savingPassword: false,

			language: props.user?.language || null,
			languageOptions: [],
            isPasswordValid: false,
		};

		this.onDrop = this.onDrop.bind(this);
	}

	componentDidMount() {
		const {
			dispatch,
			match: { params }
		} = this.props;

		dispatch(
			setBreadcrumb(() => ({
				locations: [
					{
						text: Strings.sidebar.profile,
						route: "/profile"
					}
				]
			}))
		);

		if (params?.code) {
			this.confirmEmail();
		} else {
			this.getLanguageOptions();
		}
	}

	async getLanguageOptions() {
		try {
			const response = await API.get({
				url: Endpoints.uriLanguages()
			});

			if (response.ok) {
				const { languages } = response?.data?.results;
				this.setState({ languageOptions: languages || [] });
			} else {
				notification.error({
					message: Strings.sidebar.profile,
					description: Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.profile,
				description: Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	// Error shown for a failed change-email request. The API answers with a localised message
	// (DUPLICATE_EMAIL 409, INVALID_CREDENTIALS 400, TOO_MANY_REQUESTS 429...); local strings are the fallback.
	getChangeEmailError(response: any, isConfirmation: boolean) {
		const code = response?.data?.code;

		// In phase 2 the API reports an unknown, wrong or expired code as INVALID_CREDENTIALS,
		// which would be misleading for someone who just clicked a link.
		if (isConfirmation && code === "INVALID_CREDENTIALS") return Strings.profile.emailCodeInvalid;

		if (response?.data?.message) return response.data.message;

		if (response?.status === 429 || code === "TOO_MANY_REQUESTS") return Strings.serverErrors.tooManyRequests;
		if (code === "DUPLICATE_EMAIL") return Strings.profile.emailDuplicate;
		if (code === "INVALID_CREDENTIALS") return Strings.serverErrors.invalidCredentials;

		return Strings.serverErrors.requestNotPossible;
	}

	async confirmEmail() {
		const { newEmail, password } = this.state;
		const {
			user,
			dispatch,
			match: { params }
		} = this.props;

		const isConfirmation = !!params?.code;
		const body = {} as any;

		if (isConfirmation) {
			body.code = params.code;
		} else {
			// Same normalisation as the API, so what the user sees matches what is stored.
			const normalizedEmail = String(newEmail || "").toLowerCase().trim();

			if (!normalizedEmail) {
				return notification.warn({
					message: Strings.sidebar.profile,
					description: Strings.profile.emailEmpty,
					placement: "bottomRight",
					duration: 5
				});
			}

			if (!emailIsValid(normalizedEmail)) {
				return notification.warn({
					message: Strings.errors.invalidFormat,
					description: Strings.errors.emailIsInvalid,
					placement: "bottomRight",
					duration: 5
				});
			}

			if (normalizedEmail === String(user?.email || "").toLowerCase()) {
				return notification.warn({
					message: Strings.sidebar.profile,
					description: Strings.profile.emailSameAsCurrent,
					placement: "bottomRight",
					duration: 5
				});
			}

			if (!password) {
				return notification.warn({
					message: Strings.sidebar.profile,
					description: Strings.profile.passwordEmpty,
					placement: "bottomRight",
					duration: 5
				});
			}

			body.newEmail = normalizedEmail;
			body.password = password;
		}

		dispatch(setLoader(true));

		let response: any;
		try {
			const promiseList = [
				API.post({
					url: Endpoints.uriChangeEmail(params.code),
					data: body
				})
			];

			if (isConfirmation) {
				promiseList.push(this.getLanguageOptions() as any);
			}

			[response] = await Promise.all(promiseList);

			if (response?.ok) {
				if (isConfirmation) {
					const { user: updatedUser } = response.data.results || {};
					this.setState({ ...updatedUser });
					dispatch(setUser(updatedUser));
					dispatch(replace("/profile"));

					notification.success({
						message: Strings.sidebar.profile,
						description: Strings.profile.emailChange,
						placement: "bottomRight",
						duration: 5
					});
				} else {
					// The API only answers { code: 200 } here; keep newEmail locally so the pending notice shows right away.
					dispatch(setUser({ ...user, newEmail: body.newEmail }));
					this.setState({ newEmail: "", password: "", showPassword: false });

					notification.success({
						message: Strings.sidebar.profile,
						description: Strings.profile.emailSent,
						placement: "bottomRight",
						duration: 5
					});
				}
			} else {
				notification.error({
					message: Strings.sidebar.profile,
					description: this.getChangeEmailError(response, isConfirmation),
					placement: "bottomRight",
					duration: 5
				});

				// A failed link is single-use: leave /profile/:code so a refresh does not retry it.
				if (isConfirmation) dispatch(replace("/profile"));
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.profile,
				description: this.getChangeEmailError(response, isConfirmation),
				placement: "bottomRight",
				duration: 5
			});

			if (isConfirmation) dispatch(replace("/profile"));
		}

		dispatch(setLoader(false));
	}

	profileIsValid() {
		const { name } = this.state;

		if (!name || !name.trim()) {
			notification.warn({
				message: Strings.errors.invalidFields,
				description: Strings.errors.nameIsEmpty,
				placement: "bottomRight",
				duration: 5
			});

			return false;
		}

		return true;
	}

	async saveProfile() {
		const { name, phone, photo, marketingEmail, marketingNotification, birthdate, language } = this.state;
		const { user, dispatch } = this.props;

		if (this.profileIsValid()) {
			// The e-mail is read-only here and only changes through the "Change E-mail" flow (the API ignores it anyway).
			const body = new FormData();
			body.append("name", name);
			body.append("marketingNotification", marketingNotification);
			body.append("marketingEmail", marketingEmail);

			if (phone) {
				body.append("phone", phone);
			}
			if (birthdate) {
				body.append("birthdate", birthdate);
			}

			if (photo) {
				body.append("photo", photo.file ? photo.file : photo);
			} else {
				body.append("photo", "");
			}

			if (language) {
				body.append("language", language);
			}

			this.setState({ savingProfile: true });

			const response = await API.put({
				url: Endpoints.uriUsers(user._id),
				data: body
			});

			if (response.ok) {
				const { user: newUser } = response.data.results;
				dispatch(setUser({ ...newUser }));

				notification.success({
					message: Strings.profile.header,
					description: Strings.profile.saved,
					placement: "bottomRight",
					duration: 5
				});

				this.setState({ generalChanged: false });
			}

			this.setState({ savingProfile: false });
		}
	}

	passwordIsValid() {
		const { password, newPassword } = this.state;

		if (!password || !password.trim() || !newPassword || !newPassword.trim()) {
			notification.warn({
				message: Strings.errors.invalidFields,
				description: Strings.errors.pleaseFillFormCorrectly,
				placement: "bottomRight",
				duration: 5
			});

			return false;
		}

		return true;
	}

	async changePassword() {
		const { password, newPassword } = this.state;
		const { user, dispatch } = this.props;

		if (this.passwordIsValid()) {
			const body = new FormData();
			body.append("currentPassword", JSON.stringify(password));
			body.append("password", JSON.stringify(newPassword));

			dispatch(setLoader(true));

			let response: any;
			try {
				response = await API.put({
					url: Endpoints.uriUsers(user._id),
					data: body
				});

				if (response.ok) {
					notification.success({
						message: Strings.profile.header,
						description: Strings.authentication.passwordChanged,
						placement: "bottomRight",
						duration: 5
					});

					this.setState({ password: "", newPassword: "", showPassword: false, showNewPassword: false });
				}
			} catch (err) {
				notification.error({
					message: Strings.sidebar.profile,
					description: response.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}

			dispatch(setLoader(false));
		}
	}

	onDrop(files: any) {
		try {
			const file = files[files.length - 1];

			new Compressor(file, {
				quality: 0.9,
				maxWidth: 400,
				mimeType: "image/jpeg",
				success: (result: any) => {
					this.getBase64(result).then((res) => {
						this.setState((state: any) => ({
							photo: { file: result, preview: res },
							generalChanged: true
						}));
					});
				}
			});
		} catch (err) {
			notification.warn({
				message: Strings.errors.invalidFields,
				description: Strings.errors.notSupportedFile,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	getBase64(file: any) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});
	}

	renderInfo() {
		const {
			name,
			email,
			phone,
			generalChanged,
			marketingEmail,
			marketingNotification,
			birthdate,
			language,
			languageOptions
		} = this.state;

		return (
			<div className="ProfileDetails">
				<Row gutter={[0, 20]}>
					<Col xs={24}>
						<label htmlFor="profile_input" className="InputLabel --label-required">
							{Strings.fields.name}
						</label>
						<Input
							id="profile_input"
							value={name || ""}
							className="ProfileInput"
							placeholder={Strings.fields.name}
							onChange={(e: any) => this.setState({ name: e.target.value, generalChanged: true })}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="profile_email" className="InputLabel">
							{Strings.authentication.email}
						</label>
						<Input
							id="profile_email"
							value={email || ""}
							className="ProfileInput"
							readOnly
							placeholder={Strings.authentication.email}
						/>
					</Col>
					{this.renderPendingEmailNotice()}
					<Col xs={24}>
						<label htmlFor="profile_phone" className="InputLabel">
							{Strings.profile.phone}
						</label>
						<PhoneInput
							id="profile_phone"
							defaultCountry="pt"
							value={phone || ""}
							containerClass="react-tel-input ProfilePhoneInput"
							onChange={(phone: any) => {
								this.setState({ phone, generalChanged: true });
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="profile_birthdate" className="InputLabel">
							{Strings.patients.birthdate}
						</label>
						<DatePicker
							id="patient_birthdate"
							suffixIcon={<Icons.Calendar date color="blue" />}
							className="ProfileInputMax450"
							value={birthdate ? moment.utc(birthdate) : null}
							format="DD-MM-YYYY"
							monthCellRender={(currentDate: any) => {
								return currentDate.format("MMM");
							}}
							onChange={(value: any) => {
								this.setState({ birthdate: value, generalChanged: true });
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="profile_language" className="InputLabel">
							{Strings.profile.language}
						</label>
						<Select
							id="profile_language"
							className="ProfileInputMax450"
							optionFilterProp="children"
							allowClear
							showSearch
							placeholder={Strings.profile.noLanguage}
							value={languageOptions?.length ? language || null : null}
							onChange={(value: any) => this.setState({ language: value, generalChanged: true })}
						>
							{languageOptions.map((lang: any) => (
								<Select.Option value={lang._id} key={`language_${lang._id}`}>
									{lang.name}
								</Select.Option>
							))}
						</Select>
					</Col>
					<div className="ProfileLanguageNote">{Strings.profile.languageNote}</div>
					<Col xs={24}>
						<Checkbox
							checked={marketingEmail}
							onChange={(e: any) => {
								const value = e.target.checked;
								this.setState({ marketingEmail: value, generalChanged: true });
							}}
						>
							<span>{Strings.profile.receiveEmailNotifications}</span>
						</Checkbox>
					</Col>
					<Col xs={24}>
						<Checkbox
							checked={marketingNotification}
							onChange={(e: any) => {
								const value = e.target.checked;
								this.setState({ marketingNotification: value, generalChanged: true });
							}}
						>
							<span>{Strings.profile.receiveNotifications}</span>
						</Checkbox>
					</Col>
				</Row>
				<div className="ProfileButtons">
					<button
						disabled={!generalChanged}
						className="ProfileButton --btn-primary"
						onClick={() => this.saveProfile()}
					>
						{Strings.generic.save}
					</button>
				</div>
			</div>
		);
	}

	// Warning shown while an e-mail change is waiting for confirmation through the link sent to the new address.
	renderPendingEmailNotice() {
		const { user } = this.props;

		if (!user?.newEmail) return null;

		return (
			<Col xs={24}>
				<Alert
					type="warning"
					showIcon
					message={Strings.formatString(Strings.profile.pendingEmailChange, user.newEmail)}
					description={Strings.profile.pendingEmailChangeNote}
				/>
			</Col>
		);
	}

	renderEmail() {
		const { newEmail, password, showPassword } = this.state;

		return (
			<div className="ProfileDetails">
				<Row gutter={[0, 20]}>
					{this.renderPendingEmailNotice()}
					<Col xs={24}>
						<label htmlFor="profile_new_email" className="InputLabel --label-required">
							{Strings.profile.newEmail}
						</label>
						<Input
							id="profile_new_email"
							value={newEmail || ""}
							className="ProfileInput"
							type="email"
							required
							placeholder={Strings.profile.newEmail}
							onChange={(e: any) => this.setState({ newEmail: e.target.value })}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="profile_password" className="InputLabel --label-required">
							{Strings.authentication.actualPassword}
						</label>
						<Input
							id="profile_password"
							value={password || ""}
							type={showPassword ? "text" : "password"}
							className="ProfileInput"
							suffix={
								<button
									className="PasswordInputEye"
									onClick={(e: any) => {
										e.preventDefault();
										e.stopPropagation();
										this.setState((prevState: any) => ({ showPassword: !prevState.showPassword }));
									}}
								>
									<Icons.Eye />
								</button>
							}
							placeholder={Strings.authentication.actualPassword}
							onChange={(e: any) => this.setState({ password: e.target.value })}
						/>
					</Col>
				</Row>
				<div className="ProfileButtons">
					<button
						className="ProfileButton --btn-primary"
						onClick={() => this.confirmEmail()}
						disabled={!newEmail || !password}
					>
						{Strings.fields.update}
					</button>
				</div>
			</div>
		);
	}

	renderPassword() {
		const { password, newPassword, showPassword, showNewPassword, isPasswordValid } = this.state;

		return (
			<div className="ProfileDetails">
				<Row gutter={[0, 20]}>
					<Col xs={24}>
						<label htmlFor="profile_password" className="InputLabel --label-required">
							{Strings.authentication.actualPassword}
						</label>
						<Input
							id="profile_password"
							value={password || ""}
							type={showPassword ? "text" : "password"}
							className="ProfileInput"
							suffix={
								<button
									className="PasswordInputEye"
									onClick={(e: any) => {
										e.preventDefault();
										e.stopPropagation();
										this.setState((prevState: any) => ({ showPassword: !prevState.showPassword }));
									}}
								>
									<Icons.Eye />
								</button>
							}
							placeholder={Strings.authentication.actualPassword}
							onChange={(e: any) => this.setState({ password: e.target.value })}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="profile_new_password" className="InputLabel --label-required">
							{Strings.profile.newPassword}
						</label>
						<Password
							className="ProfileInput"
							password={newPassword || ""}
							showPassword={showPassword}
							onPasswordChange={(newPassword: string, isValid: boolean) => {
								this.setState({ newPassword: newPassword, isPasswordValid: isValid });
							}}
							onShowPasswordChange={(value: boolean) => this.setState({ showPassword: value })}
							placeholder={Strings.profile.newPassword}
						/>
					</Col>
				</Row>
				<div className="ProfileButtons">
					<button
						className="ProfileButton --btn-primary"
						onClick={() => this.changePassword()}
						disabled={!password || !newPassword || !isPasswordValid}
					>
						{Strings.fields.update}
					</button>
				</div>
			</div>
		);
	}

	render() {
		const { photo, activeTab } = this.state;
		const { mobile } = this.props;

		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.generic.profile}</title>
					<meta name="description" content="Description of Profile" />
				</Helmet>
				<div className="ProfileContent">
					<h1 className="GenericTitle">{Strings.generic.profile}</h1>
					<div className="ProfileDivision">
						<div className="ProfilePictureWrapper">
							<img src={photo?.preview || photo || ProfileImagePlaceholder} alt="profile avatar" />
							<Dropzone
								accept="image/jpg, image/jpeg, image/png"
								className="GenericImageUpload"
								id="image_upload"
								onDrop={(files: any) => this.onDrop(files)}
							>
								<button>{photo ? Strings.profile.editPhoto : Strings.profile.addPhoto}</button>
							</Dropzone>
						</div>
						<div className="ProfileSettings">
							<Tabs
								defaultActiveKey="info"
								onChange={(key: string) => {
									if (key !== activeTab) {
										this.setState({
											activeTab: key,
											password: "",
											showPassword: false,
											newPassword: "",
											showNewPassword: false,
											newEmail: ""
										});
									}
								}}
								centered={mobile}
							>
								<Tabs.TabPane
									tab={
										<div className="TabIconContainer">
											<Icons.Pencil
												color={activeTab === "info" ? Variables.primaryColor : false}
											/>
											{mobile && activeTab === "info" ? <p>{Strings.profile.myAccount}</p> : ""}
											{!mobile && <p>{Strings.profile.myAccount}</p>}
										</div>
									}
									key="info"
								/>
								<Tabs.TabPane
									tab={
										<div className="TabIconContainer">
											<Icons.Message
												tabs
												color={activeTab === "email" ? Variables.primaryColor : false}
											/>
											{mobile && activeTab === "email" ? (
												<p>{Strings.profile.changeEmail}</p>
											) : (
												""
											)}
											{!mobile && <p>{Strings.profile.changeEmail}</p>}
										</div>
									}
									key="email"
								/>
								<Tabs.TabPane
									tab={
										<div className="TabIconContainer">
											<Icons.Lock
												tabs
												color={activeTab === "password" ? Variables.primaryColor : false}
											/>
											{mobile && activeTab === "password" ? (
												<p>{Strings.profile.changePassword}</p>
											) : (
												""
											)}
											{!mobile && <p>{Strings.profile.changePassword}</p>}
										</div>
									}
									key="password"
								/>
							</Tabs>
							{activeTab === "info" && this.renderInfo()}
							{activeTab === "email" && this.renderEmail()}
							{activeTab === "password" && this.renderPassword()}
						</div>
					</div>
				</div>
				{mobile && <Footer />}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: any) => ({
	user: state.user,
	language: state.language,
	mobile: state.mobile
});

export default connect(mapStateToProps)(Profile);
